import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import api from "../connector/api";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import ChangePasswordModal from "./components/ChangePasswordModal";

import { 
  FiUser, 
  FiCamera, 
  FiPhone, 
  FiCalendar, 
  FiBriefcase, 
  FiLock,
  FiAlertTriangle,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiMail,
  FiAward,
  FiShield,
  FiUserCheck,
  FiChevronDown,
  FiCheck
} from "react-icons/fi";

// Exported as a named component instead of default export
export function CustomSelect({ options, value, onChange, placeholder = "Select..." }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const selectedOption = options.find((opt) => String(opt.value) === String(value));

  return (
    <div className="relative inline-block text-left w-44" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full flex items-center justify-between gap-2 px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-md text-xs font-medium text-zinc-700 hover:bg-zinc-100 transition-colors shadow-xs"
      >
        <span className="truncate">{selectedOption ? selectedOption.label : placeholder}</span>
        <FiChevronDown className={`w-3.5 h-3.5 text-zinc-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-zinc-200 rounded-md shadow-lg z-30 py-1 divide-y divide-zinc-50 scrollbar-thin scrollbar-thumb-zinc-300">
          {options.map((option) => {
            const isSelected = String(option.value) === String(value);
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3 py-1.5 text-xs flex items-center justify-between hover:bg-zinc-100 transition-colors ${
                  isSelected ? "font-semibold text-zinc-900 bg-zinc-50" : "text-zinc-600"
                }`}
              >
                <span className="truncate">{option.label}</span>
                {isSelected && <FiCheck className="w-3.5 h-3.5 text-zinc-800" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Active Tab State
  const [activeTab, setActiveTab] = useState("overview");

  // Layout states
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [view, setView] = useState("profile");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentUser, setCurrentUser] = useState(null);

  // User & Avatar states
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Modal states
  const [openChangePasswordModal, setOpenChangePasswordModal] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);

  // Header & Session State
  const [permissions, setPermissions] = useState([]);
  const [user, setUser] = useState({ first_name: "Achmad", last_name: "Hakim", email: "achmadhakim@gmail.com" });
  const notificationDropdownRef = useRef(null);
  const [notifOpen, setNotifOpen] = useState(false);

  // Terms State
  const [activeTermData, setActiveTermData] = useState(null);
  const [selectedTerm, setSelectedTerm] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [yearTermsList, setYearTermsList] = useState([]);

  // Clean up object URL memory leak on unmount or preview change
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  // Load Session Data
  useEffect(() => {
    const storedPerms = sessionStorage.getItem("permissions");
    const storedUser = sessionStorage.getItem("user");
    
    if (storedPerms) {
      try { setPermissions(JSON.parse(storedPerms)); } 
      catch (err) { console.error("Failed to parse permissions:", err); }
    }

    if (storedUser) {
      try {
        const userObj = JSON.parse(storedUser);
        setUser({
          first_name: userObj.first_name || "Achmad",
          last_name: userObj.last_name || "Hakim",
          username: userObj.username || "achmadhakim",
          email: userObj.email || "achmadhakim@gmail.com"
        });
      } catch (err) {
        console.error("Failed to parse user session:", err);
      }
    }
  }, []);

  // Fetch API Data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [userRes, currentTermRes, allTermsRes] = await Promise.all([
          api.get("/accounts/me/"),
          api.get("/year-terms/current/").catch(() => null),
          api.get("/year-terms/").catch(() => null)
        ]);

        if (userRes?.data) {
          setUserInfo({
            ...userRes.data.user,
            avatar: userRes.data.user?.avatar || "",
          });
          setCurrentUser({
            ...userRes.data.user,
            permissions: userRes.data.permissions || []
          });
        }

        if (currentTermRes?.data) setActiveTermData(currentTermRes.data);
        if (allTermsRes?.data) {
          setYearTermsList(allTermsRes.data?.results || allTermsRes.data || []);
        }
      } catch (err) {
        console.error("Failed to fetch profile details:", err);
        toast.error("Failed to load profile information");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const termDisplay = activeTermData?.term_name || activeTermData?.term?.name || "N/A";
  const yearDisplay = activeTermData?.school_year_name || activeTermData?.school_year?.name || "N/A";
  const employee = userInfo?.employee;

  // Single file handling function
  const handlePhotoSelectAndUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
    setUploading(true);

    const formData = new FormData();
    formData.append("profile_picture", file);

    try {
      const res = await api.patch("/accounts/me/avatar/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUserInfo((prev) => ({ ...prev, avatar: res.data.avatar }));
      toast.success("Profile photo updated successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to upload avatar");
    } finally {
      setPreview(null);
      setUploading(false);
    }
  };

  const handleAvatarRemove = async () => {
    if (!userInfo?.avatar) return;
    setUploading(true);
    try {
      await api.delete("/accounts/me/avatar/");
      setUserInfo((prev) => ({ ...prev, avatar: "" }));
      toast.success("Profile photo removed");
      setOpenDeleteConfirm(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to remove avatar");
    } finally {
      setUploading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = "/login";
  };

  // Options mapping
  const termOptions = [
    { label: "All Terms", value: "all" },
    ...(yearTermsList || []).map((t) => ({
      label: `${t.term_name || t.term?.name || "Term"} (${t.school_year_name || t.school_year?.name || ""})`,
      value: String(t.id),
    })),
  ];

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const monthOptions = [
    { label: "All Months", value: "all" },
    ...monthNames.map((month, idx) => ({
      label: month,
      value: String(idx),
    })),
  ];

  const filteredAttendance = (employee?.attendance_records || []).filter((rec) => {
    const recDate = new Date(rec.date);

    // 1. Month Filter
    if (selectedMonth !== "all" && recDate.getMonth() !== parseInt(selectedMonth, 10)) {
      return false;
    }

    // 2. YearTerm Filter
    if (selectedTerm !== "all") {
      // If year_term is undefined, it won't crash and will fail gracefully until backend is updated
      if (!rec.year_term) return false;

      // Extract ID whether it's an integer or nested object
      const termId = typeof rec.year_term === "object" ? rec.year_term.id : rec.year_term;

      if (String(termId) !== String(selectedTerm)) {
        return false;
      }
    }

    return true;
  });

  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const monthlyAttendance = employee?.attendance_records?.filter((rec) => {
    const date = new Date(rec.date);
    return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
  }) || [];

  // Calculate dynamic counts based on the active filters
  const countPresent = filteredAttendance.filter((r) => r.status === "Present").length;
  const countLate = filteredAttendance.filter((r) => r.status === "Late").length;
  const countAbsent = filteredAttendance.filter((r) => r.status === "Absent").length;

  return (
    <div className="h-screen w-screen bg-zinc-50 flex flex-row overflow-hidden antialiased text-zinc-900 font-dynamic-title">
      <Toaster position="top-right" />

      <Sidebar
        permissions={permissions}
        user={user || {}}
        handleLogout={handleLogout}
      />

      <div className="flex-1 flex flex-col h-full min-w-0 overflow-hidden">
        <Header 
          currentUser={currentUser}
          currentView={view}
          setView={setView}
          onLogout={handleLogout}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          loadingPeriod={false}
          yearDisplay={yearDisplay}
          termDisplay={termDisplay}
          searchQuery={searchQuery}
          setSearchQuery={(e) => setSearchQuery(e.target.value)}
          notifOpen={notifOpen}
          setNotifOpen={setNotifOpen}
          notificationDropdownRef={notificationDropdownRef}
        />

        <main className="flex-1 overflow-y-auto w-full px-4 sm:px-6 lg:px-8 py-6 custom-scroll">
          <div className="max-w-6xl mx-auto space-y-6">
            
            {/* Profile Header */}
            <div className="bg-white border border-zinc-200/80 rounded-lg shadow-xs overflow-hidden">
              <div className="h-40 bg-[#002855] relative" />
              
              <div className="px-6 pb-5 pt-0 relative flex flex-col sm:flex-row sm:items-end justify-between gap-4 border-b border-zinc-100">
                <div className="flex flex-col sm:flex-row items-center sm:items-end gap-5 -mt-16 sm:-mt-12">
                  
                  <div className="relative group">
                    <div 
                      onClick={() => !uploading && !loading && fileInputRef.current?.click()}
                      className="w-32 h-32 rounded-xl bg-zinc-100 border-4 border-white shadow-md flex items-center justify-center cursor-pointer overflow-hidden transition-all duration-200 group-hover:opacity-95"
                    >
                      {loading ? (
                        <div className="w-full h-full bg-zinc-200 animate-pulse" />
                      ) : preview ? (
                        <img src={preview} alt="Preview" className="object-cover w-full h-full" />
                      ) : userInfo?.avatar ? (
                        <img src={userInfo.avatar} alt="User Avatar" className="object-cover w-full h-full" />
                      ) : (
                        <FiUser className="w-14 h-14 text-zinc-400" />
                      )}

                      {!loading && (
                        <div className="absolute inset-0 bg-zinc-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white text-xs font-bold uppercase tracking-wider gap-1.5">
                          <FiCamera className="w-5 h-5" />
                          <span>{uploading ? "Uploading..." : "Update"}</span>
                        </div>
                      )}
                    </div>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={uploading || loading}
                      onChange={handlePhotoSelectAndUpload}
                    />
                  </div>

                  <div className="text-center sm:text-left space-y-1 mb-1">
                    {loading ? (
                      <div className="space-y-1.5">
                        <div className="h-6 w-56 bg-zinc-200 rounded animate-pulse" />
                        <div className="h-4 w-40 bg-zinc-100 rounded animate-pulse" />
                      </div>
                    ) : (
                      <>
                        <h1 className="text-lg font-dynamic-title font-bold tracking-tight text-zinc-900">
                          {employee?.last_name || ""}, {employee?.first_name || ""}
                        </h1>
                        <p className="text-xs font-medium text-zinc-600 flex items-center justify-center sm:justify-start gap-1.5">
                          <FiMail className="w-4 h-4 text-zinc-400" />
                          <span>{employee?.email || userInfo?.email}</span>
                        </p>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-center gap-2 self-center sm:self-end pb-1">
                  {!loading && (
                    <>
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-zinc-100 text-zinc-700 border border-zinc-200/80">
                        <FiShield className="w-4 h-4 text-zinc-500" />
                        {userInfo?.is_superuser ? "Administrator" : "Standard User"}
                      </span>
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/60">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                        {employee?.status || "Active"}
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Navigation Tabs */}
              <div className="flex border-t border-zinc-100 px-6 gap-6">
                {[
                  { id: "overview", label: "General", icon: FiUserCheck },
                  { id: "attendance", label: "Attendance", icon: FiClock },
                  { id: "security", label: "Account Security", icon: FiLock },
                ].map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`py-3.5 text-xs font-semibold inline-flex items-center gap-2 border-b-2 transition-colors cursor-pointer ${
                        isActive 
                          ? "border-[#002855] text-[#002855]" 
                          : "border-transparent text-zinc-600 hover:text-zinc-900"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Overview Tab */}
            {activeTab === "overview" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border border-zinc-200/80 rounded-lg p-5 shadow-xs space-y-4">
                  <div className="flex items-center gap-2 border-b border-zinc-100 pb-3">
                    <FiUser className="w-4 h-4 text-zinc-400" />
                    <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Personal Details</h3>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-0.5">
                      <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Phone Number</span>
                      <p className="text-xs font-semibold text-zinc-800 flex items-center gap-1.5">
                        <FiPhone className="w-3.5 h-3.5 text-zinc-400" />
                        {employee?.phone || "Not set"}
                      </p>
                    </div>

                    <div className="space-y-0.5">
                      <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Gender</span>
                      <p className="text-xs font-semibold text-zinc-800">
                        {employee?.gender || "Not specified"}
                      </p>
                    </div>

                    <div className="space-y-0.5">
                      <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Date of Birth</span>
                      <p className="text-xs font-semibold text-zinc-800 flex items-center gap-1.5">
                        <FiCalendar className="w-3.5 h-3.5 text-zinc-400" />
                        {employee?.dob || "—"}
                      </p>
                    </div>

                    <div className="space-y-0.5">
                      <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Hire Date</span>
                      <p className="text-xs font-semibold text-zinc-800 flex items-center gap-1.5">
                        <FiBriefcase className="w-3.5 h-3.5 text-zinc-400" />
                        {employee?.hire_date || "—"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-zinc-200/80 rounded-lg p-5 shadow-xs space-y-4">
                  <div className="flex items-center gap-2 border-b border-zinc-100 pb-3">
                    <FiAward className="w-4 h-4 text-zinc-400" />
                    <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Organization & Access</h3>
                  </div>

                  <div className="space-y-2.5 text-xs">
                    <div className="flex justify-between py-1 border-b border-zinc-50">
                      <span className="text-zinc-600 font-medium">Username</span>
                      <span className="font-mono font-semibold text-zinc-900">{userInfo?.username || user.username}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-zinc-50">
                      <span className="text-zinc-600 font-medium">Account Access</span>
                      <span className="font-semibold text-zinc-900">{userInfo?.is_superuser ? "Super Administrator" : "Standard Access"}</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-zinc-600 font-medium">Active Academic Term</span>
                      <span className="font-semibold text-zinc-900">
                        {activeTermData && termDisplay !== "N/A"
                          ? `${termDisplay} (${yearDisplay})`
                          : "No Active Term"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Attendance Tab */}
            {activeTab === "attendance" && (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-white p-3 border border-zinc-200/80 rounded-lg shadow-xs">
                  <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
                    <div className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200/60 rounded-md text-xs font-semibold whitespace-nowrap">
                      <FiCheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Present: <strong className="text-emerald-900">{countPresent}</strong></span>
                    </div>

                    <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-700 border border-amber-200/60 rounded-md text-xs font-semibold whitespace-nowrap">
                      <FiAlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                      <span>Late: <strong className="text-amber-900">{countLate}</strong></span>
                    </div>

                    <div className="flex items-center gap-1.5 px-2.5 py-1 bg-rose-50 text-rose-700 border border-rose-200/60 rounded-md text-xs font-semibold whitespace-nowrap">
                      <FiXCircle className="w-3.5 h-3.5 text-rose-600" />
                      <span>Absent: <strong className="text-rose-900">{countAbsent}</strong></span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <CustomSelect
                      options={termOptions}
                      value={selectedTerm}
                      onChange={(val) => setSelectedTerm(val)}
                      placeholder="Select Term"
                    />

                    <CustomSelect
                      options={monthOptions}
                      value={selectedMonth}
                      onChange={(val) => setSelectedMonth(val)}
                      placeholder="Select Month"
                    />
                  </div>
                </div>

                <div className="bg-white border border-zinc-200/80 rounded-lg shadow-xs overflow-hidden">
                  {loading ? (
                    <div className="p-6 space-y-3">
                      <div className="h-7 bg-zinc-100 rounded animate-pulse" />
                      <div className="h-7 bg-zinc-50 rounded animate-pulse" />
                    </div>
                  ) : filteredAttendance.length > 0 ? (
                    <div className="max-h-96 overflow-y-auto custom-scroll">
                      <table className="w-full text-left border-collapse text-xs relative">
                        <thead className="sticky top-0 z-10 bg-zinc-50 shadow-xs">
                          <tr className="border-b border-zinc-200 text-[10px] font-bold uppercase tracking-wider text-zinc-400">
                            <th className="py-2.5 px-4">Date</th>
                            <th className="py-2.5 px-4">Comment</th>
                            <th className="py-2.5 px-4">Recorded By</th>
                            <th className="py-2.5 px-4 text-right">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100">
                          {filteredAttendance.map((rec, index) => {
                            const statusStyle = {
                              Present: "bg-emerald-50 text-emerald-700 border-emerald-200/60",
                              Late: "bg-amber-50 text-amber-700 border-amber-200/60",
                              Absent: "bg-rose-50 text-rose-700 border-rose-200/60"
                            }[rec.status] || "bg-zinc-100 text-zinc-600";

                            // Safely extract username whether rec.recorded_by is a string or object
                            const getRecorderName = (recordedBy) => {
                              if (!recordedBy) return "—";
                              if (typeof recordedBy === "object") {
                                const fullName = `${recordedBy.first_name || ""} ${recordedBy.last_name || ""}`.trim();
                                return fullName || recordedBy.username || "—";
                              }
                              return recordedBy;
                            };

                            const recorderName = getRecorderName(rec.recorded_by);

                            return (
                              <tr key={rec.id || index} className="hover:bg-zinc-50/70 transition-colors">
                                <td className="py-2.5 px-4 font-mono font-medium text-zinc-800">{rec.date}</td>
                                <td className="py-2.5 px-4 text-zinc-600">{rec.comment || "—"}</td>
                                
                                {/* Recorded By Column */}
                                <td className="py-2.5 px-4 text-zinc-600 font-medium">
                                  {(() => {
                                    const recBy = rec.recorded_by;
                                    if (!recBy) return "—";
                                    
                                    if (typeof recBy === "object") {
                                      const fullName = `${recBy.last_name || ""}, ${recBy.first_name || ""}`.trim();
                                      return fullName || recBy.username || "—";
                                    }
                                    
                                    return String(recBy);
                                  })()}
                                </td>

                                <td className="py-2.5 px-4 text-right">
                                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${statusStyle}`}>
                                    {rec.status}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="py-10 flex flex-col items-center justify-center text-center">
                      <FiClock className="w-6 h-6 text-zinc-300 mb-1.5" />
                      <p className="text-zinc-500 text-xs font-medium">No matching attendance records</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Account Security Tab */}
            {activeTab === "security" && (
              <div className="bg-white border border-zinc-200/80 rounded-lg p-5 sm:p-6 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-zinc-900">
                    <FiLock className="w-4 h-4 text-zinc-700" />
                    <h3 className="text-sm font-semibold">Password & Authentication</h3>
                  </div>
                  <p className="text-xs text-zinc-600 max-w-md">
                    Ensure your account is using a strong password. We recommend updating it regularly.
                  </p>
                </div>

                <button
                  onClick={() => setOpenChangePasswordModal(true)}
                  disabled={loading}
                  className="px-3.5 py-2 bg-rose-900 hover:bg-rose-800 text-white font-mono font-medium text-xs rounded-xl shadow-xs transition-colors cursor-pointer disabled:opacity-50"
                >
                  Change Password
                </button>
              </div>
            )}

          </div>
        </main>
      </div>

      {/* Delete Confirmation Modal */}
      {openDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 backdrop-blur-xs animate-in fade-in duration-150">
          <div className="bg-white border border-zinc-200 rounded-xl p-5 max-w-sm w-full mx-4 shadow-xl space-y-4">
            <div className="flex items-center gap-2 text-red-600">
              <FiAlertTriangle className="w-5 h-5" />
              <h3 className="text-sm font-bold">Remove Profile Photo?</h3>
            </div>
            <p className="text-xs text-zinc-600 leading-relaxed">
              This action will permanently remove your display picture and reset your avatar to default.
            </p>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-100">
              <button
                onClick={() => setOpenDeleteConfirm(false)}
                className="px-3 py-1.5 text-xs font-semibold text-zinc-600 hover:text-zinc-900 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleAvatarRemove}
                disabled={uploading}
                className="px-3 py-1.5 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-md shadow-xs transition-colors cursor-pointer disabled:opacity-50"
              >
                {uploading ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={openChangePasswordModal}
        onClose={() => setOpenChangePasswordModal(false)}
      />
    </div>
  );
}